// CONTRACT:
// PURPOSE: [Клиентский компонент фильтров для страницы рейтинга ЧОП]
// SCOPE: [rating-filters, search, region, status]

"use client";

import { useRouter, useSearchParams, usePathname } from "next/navigation";
import { useCallback, useState, useTransition } from "react";
import { Search, SlidersHorizontal } from "lucide-react";
import { normalizeRegion } from "@oxpa/shared/lib/region-utils";

// AI_REFLECTION: [SCORE: Q=8, R=8, M=7] [DEPTH: DEEP] [NOTE: debounce через setTimeout, обновление URL searchParams]

interface RatingFiltersProps {
  regions: string[];
}

export function RatingFilters({ regions }: RatingFiltersProps) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [isPending, startTransition] = useTransition();

  const [searchValue, setSearchValue] = useState(
    searchParams.get("q") || ""
  );
  const [debounceTimer, setDebounceTimer] = useState<NodeJS.Timeout | null>(
    null
  );

  const currentRegion = searchParams.get("region") || "";
  const currentStatus = searchParams.get("status") || "";

  const updateParams = useCallback(
    (key: string, value: string) => {
      const params = new URLSearchParams(searchParams.toString());
      if (value) {
        params.set(key, value);
      } else {
        params.delete(key);
      }
      // Сбросить на первую страницу при смене фильтра
      params.delete("page");

      startTransition(() => {
        router.push(`${pathname}?${params.toString()}`);
      });
    },
    [searchParams, pathname, router]
  );

  const handleSearch = useCallback(
    (value: string) => {
      setSearchValue(value);
      if (debounceTimer) clearTimeout(debounceTimer);
      const timer = setTimeout(() => {
        updateParams("q", value);
      }, 400);
      setDebounceTimer(timer);
    },
    [debounceTimer, updateParams]
  );

  return (
    <div className="border-b border-white/10 py-4">
      <div className="container mx-auto px-6">
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
          {/* Иконка фильтров */}
          <div className="flex items-center gap-2 text-white/30">
            <SlidersHorizontal className="w-3.5 h-3.5" />
            <span className="text-xs uppercase tracking-wider">Фильтры</span>
          </div>

          {/* Фильтр по региону */}
          <select
            value={currentRegion}
            onChange={(e) => updateParams("region", e.target.value)}
            className="bg-transparent text-xs border border-white/10 rounded-sm px-3 py-1.5 text-white/60 uppercase tracking-wider appearance-none cursor-pointer hover:bg-white/5 transition-colors min-w-[160px]"
          >
            <option value="" className="bg-black">
              Все регионы
            </option>
            {regions.map((r) => (
              <option key={r} value={r} className="bg-black">
                {normalizeRegion(r)}
              </option>
            ))}
          </select>

          {/* Фильтр по статусу */}
          <select
            value={currentStatus}
            onChange={(e) => updateParams("status", e.target.value)}
            className="bg-transparent text-xs border border-white/10 rounded-sm px-3 py-1.5 text-white/60 uppercase tracking-wider appearance-none cursor-pointer hover:bg-white/5 transition-colors"
          >
            <option value="" className="bg-black">
              Все статусы
            </option>
            <option value="ACTIVE" className="bg-black">
              Действующие
            </option>
            <option value="INACTIVE" className="bg-black">
              Ликвидированные
            </option>
          </select>

          {/* Поиск */}
          <div className="ml-auto flex items-center gap-2 border border-white/10 rounded-sm px-3 py-1.5 w-full sm:w-auto sm:min-w-[280px]">
            <Search className="w-3 h-3 text-white/30 flex-shrink-0" />
            <input
              type="text"
              placeholder="Поиск ЧОП по названию или ИНН..."
              value={searchValue}
              onChange={(e) => handleSearch(e.target.value)}
              className="bg-transparent text-xs text-white/70 placeholder:text-white/30 outline-none w-full"
            />
            {isPending && (
              <div className="w-3 h-3 border border-white/30 border-t-white rounded-full animate-spin flex-shrink-0" />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
